// src/components/app/SafeAreaView/SafeAreaView.tsx

import type { CSSProperties, ReactNode } from "react";
import "./SafeAreaView.scss";

interface SafeAreaViewProps {
    children: ReactNode;
    className?: string;
    /** Extra insets (px) on top of Telegram's own safe-area values. */
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
    /** Ignore Telegram's top/bottom safe-area inset for this instance. */
    skipTop?: boolean;
    skipBottom?: boolean;
}

/**
 * Shrinks/repositions its content so it never sits under Telegram's device
 * safe area (notches, rounded corners) or, in fullscreen mode, under
 * Telegram's own UI (the transparent header and native close button).
 *
 * Wrap page content (or specific overlays like a close button) in this
 * component instead of relying on `100vh`/`100%` directly.
 */
export default function SafeAreaView({
    children,
    className,
    top = 0,
    right = 0,
    bottom = 0,
    left = 0,
    skipTop = false,
    skipBottom = false,
}: SafeAreaViewProps) {
    const style = {
        "--inset-top": `${top}px`,
        "--inset-right": `${right}px`,
        "--inset-bottom": `${bottom}px`,
        "--inset-left": `${left}px`,
    } as CSSProperties;

    const classes = [
        "safe-area-view",
        skipTop && "safe-area-view--skip-top",
        skipBottom && "safe-area-view--skip-bottom",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <div className={classes} style={style}>
            {children}
        </div>
    );
}
